<!-- last modified by AI tools on 2026-10-02 -->
<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useLiveStatus } from '@/composables/useLiveStatus';

const props = defineProps<{
  /** Last trade time (epoch seconds) of the currently-selected contract. */
  lastTradeTime?: number;
  /** Start of trading (epoch seconds) of the currently-selected contract, when known. */
  startTime?: number;
  /** Compact layout (mobile): drops the trailing countdown/closed-suffix text so
   *  the badge fits alongside a truncated breadcrumb on narrow viewports. */
  compact?: boolean;
}>();

const { t } = useI18n();
const { countdownLabel, opensInLabel, isUrgent, isClosed, isOpeningSoon, isClosingSoon, isVisible } = useLiveStatus(
  () => props.lastTradeTime,
  () => props.startTime,
);
</script>

<template>
  <div
    v-if="isVisible"
    :class="[
      'inline-flex items-center rounded-lg border',
      compact ? 'gap-1.5 px-2 py-0.5' : 'gap-2 px-2.5 py-1',
      isOpeningSoon
        ? 'bg-surface-lowest border-(--p-content-border-color)'
        : isClosed
          ? 'bg-surface-low border-transparent'
          : isUrgent
            ? 'bg-surface-lowest border-(--p-red200) dark:border-(--p-red100)'
            : 'bg-surface-lowest border-(--p-content-border-color)',
    ]"
  >
    <span v-if="isOpeningSoon" class="inline-flex h-2 w-2 flex-shrink-0 rounded-full bg-on-surface-tertiary" />
    <span v-else-if="!isClosed" class="live-dot relative flex h-2 w-2 flex-shrink-0">
      <span class="live-dot__glow" />
      <span class="relative inline-flex h-2 w-2 rounded-full bg-(--p-red200) dark:bg-(--p-red100)" />
    </span>
    <span v-else class="inline-flex h-2 w-2 flex-shrink-0 rounded-full bg-on-surface-tertiary" />
    <span v-if="isOpeningSoon" class="text-xs font-bold uppercase tracking-[0.05em] text-on-surface-tertiary">
      {{ t('marketOpensSoon') }}
    </span>
    <span
      v-else
      :class="[
        'text-xs font-bold uppercase tracking-[0.05em]',
        isClosed ? 'text-on-surface-tertiary' : 'text-(--p-red200) dark:text-(--p-red100)',
      ]"
    >
      {{ isClosed ? t('closed') : t('live') }}
    </span>
    <span v-if="isOpeningSoon" class="text-xs text-on-surface tabular-nums">
      <template v-if="compact">{{ opensInLabel }}</template>
      <template v-else>{{ t('marketOpensIn', { countdown: opensInLabel }) }}</template>
    </span>
    <span
      v-else-if="isClosed ? !compact : isClosingSoon"
      :class="[
        'text-xs',
        isClosed
          ? 'text-on-surface-secondary'
          : isUrgent
            ? 'text-(--p-red200) dark:text-(--p-red100) tabular-nums'
            : 'text-on-surface tabular-nums',
      ]"
    >
      <template v-if="isClosed">{{ t('marketIsClosedForTrading') }}</template>
      <template v-else-if="compact">{{ countdownLabel }}</template>
      <template v-else>{{ t('marketClosesIn', { countdown: countdownLabel }) }}</template>
    </span>
  </div>
</template>

<style scoped>
/* Soft red radial glow that breathes around the LIVE indicator dot. The blurred
   halo — not a hard-edged expanding ring — is what the Figma live-badge spec
   calls for; the LIVE dot is always red regardless of countdown urgency. */
.live-dot__glow {
  --live-status-red: var(--p-red200, #dc2626);

  position: absolute;
  inset: -2px;
  border-radius: 9999px;
  filter: blur(2px);
  opacity: 0.6;
  pointer-events: none;
  animation: live-dot-glow 1.8s ease-in-out infinite;
  background: radial-gradient(
    circle,
    var(--live-status-red) 0%,
    color-mix(in srgb, var(--live-status-red) 55%, transparent) 55%,
    transparent 80%
  );
}

:global(.dark) .live-dot__glow {
  --live-status-red: var(--p-red100, #ef4444);
}

@keyframes live-dot-glow {
  0%,
  100% {
    transform: scale(0.9);
    opacity: 0.35;
  }

  50% {
    transform: scale(1.1);
    opacity: 0.65;
  }
}

@media (prefers-reduced-motion: reduce) {
  .live-dot__glow {
    animation: none;
    opacity: 0.55;
  }
}
</style>
